<!--suppress HtmlUnknownTag -->
<vuejs3-todo>
    Ready
</vuejs3-todo>

<template>
    <section class="login-page">
        <p class="container-demo">
            {{ i18n.demo.line1 }}<br/>
            <a href="https://www.echoline.fr/demo/"
               target="_blank"
            >{{ i18n.demo.line2 }}</a>
        </p>
        <form @submit.prevent="getToken"
              v-show="mode === 'login'"
              class="container-login"
        >
            <img src="https://api.echoline.fr/static/logo.png"
                 alt="Echoline logo"
            />
            <!--suppress HtmlFormInputWithoutLabel -->
            <input type="email"
                   :placeholder="i18n.login.email"
                   :aria-label="i18n.login.email"
                   v-model="user.email"
                   data-cy="email-login"
                   required
            />
            <!--suppress HtmlFormInputWithoutLabel -->
            <input type="password"
                   :placeholder="i18n.login.password"
                   :aria-label="i18n.login.password"
                   v-model="user.password"
                   data-cy="password-login"
                   required
            />
            <p class="label-error"
               v-if="loginError"
            >{{ i18n.login.error }}</p>
            <div>
                <router-link :to="`/forgotten-password/request/${user.email}`"
                             data-cy="forgotten-password"
                >
                    {{ i18n.login.forgotten }}
                </router-link>
                <button type="submit"
                        data-cy="submit-login"
                >{{ i18n.login.submit }}</button>
            </div>
        </form>
        <form @submit.prevent="consent"
              v-show="mode === 'cookies'"
              class="container-cookies"
        >
            <p>{{ i18n.cookies.summary }}</p>
            <ul>
                <li>
                    <app-toggle v-model="cookies.ga"></app-toggle> {{ i18n.cookies.ga }}
                </li>
            </ul>
            <p>{{ i18n.cookies.change }}</p>
            <div>
                <button type="submit" data-cy="cookie">{{ i18n.cookies.submit }}</button>
            </div>
        </form>
    </section>
</template>

<script>
    import axios from "@/outside/axios";
    import AppToggle from "@/common/components/app-toggle";
    import CookiesConsentRepository from "@/common/models/CookiesConsentRepository";

    const i18n = {
        de: {
            cookies: {
                change: "Sie können Ihre Entscheidung jederzeit widerrufen, indem Sie Ihr Benutzer-Profil anpassen.",
                ga: "Google Analytics (Nutzungs- und Leistungsstatistiken)",
                submit: "Weiter",
                summary: `
                    Die Echoline-Anwendung verwendet bestimmte Tools von Drittanbietern, für die Cookies auf Ihrem
                    Browser binterlegt werden. Zuvor ist dafür in Anwendung der europäischen und französischen
                    Gesetzgebung Ihr Einverständnis erforderlich. Wählen Sie die Cookies aus, die Sie uns erlauben, auf
                    diesem Rechner zu hinterlegen.
                `
            },
            demo: {
                line1: "Noch nicht angemeldet?",
                line2: "Fordern Sie eine Demo an →"
            },
            login: {
                email: "E-Mail-Adresse",
                error: "Ihre E-Mail-Adresse oder das Passwort ist falsch",
                forgotten: "Passwort vergessen",
                password: "Passwort",
                submit: "Log-in"
            }
        },
        en: {
            cookies: {
                change: "You will be able to change your mind at any time by updating your user profile.",
                ga: "Google Analytics (usage and performances statistics)",
                submit: "Continue",
                summary: `
                    Echoline app uses some third-party tools requiring cookies storage in your browser. In this context,
                    and in compliance with european and french regulations, we need your consent first. Please select
                    the type of cookies you allow us to store in your browser.
                `
            },
            demo: {
                line1: "Not signed-up yet?",
                line2: "Ask for a demo →"
            },
            login: {
                email: "Email address",
                error: "Your email address or password is incorrect",
                forgotten: "Forgot your password?",
                password: "Password",
                submit: "Login"
            }
        },
        fr: {
            cookies: {
                change: `
                    Vous pourrez revenir sur votre décision à tout moment en mettant à jour votre profil utilisateur.
                `,
                ga: "Google Analytics (mesure de l'utilisation et des performances)",
                submit: "Continuer",
                summary: `
                    L'application Echoline utilise certains outils tiers nécessitant le dépôt de cookies sur votre
                    navigateur. Dans ce cadre, et en application des législations européenne et française, votre
                    consentement préalable est nécessaire. Sélectionnez les cookies que vous nous autorisez à déposer
                    sur cet ordinateur.
                `
            },
            demo: {
                line1: "Vous n'êtes pas encore inscrit ?",
                line2: "Demander une démonstration →"
            },
            login: {
                email: "Adresse email",
                error: "Votre adresse email ou votre mot de passe est incorrect",
                forgotten: "Mot de passe oublié ?",
                password: "Mot de passe",
                submit: "Connexion"
            }
        }
    };

    export default {
        components: {
            AppToggle
        },
        computed: {
            i18n() {
                return this.$i18n.strings(i18n, this.$options.name);
            }
        },
        data() {
            return {
                cookies: new CookiesConsentRepository(),
                loginError: false,
                mode: "login",
                user: {
                    email: "",
                    password: ""
                }
            };
        },
        methods: {
            /**
             * Saves cookies consent data to `localStorage`.
             * @return {void}
             */
            consent() {
                window.localStorage.setItem("cookies_consent", JSON.stringify(this.cookies));
                this.redirect();
            },
            /**
             * Fetches a JWT from Echoline API using current email and password.
             * @return {Promise}
             */
            getToken() {
                return axios.post("/rpc", {
                    action: "customers.gettoken",
                    params: this.user
                }).then((response) => {
                    window.localStorage.setItem("token", response.data);

                    if (!window.localStorage.getItem("cookies_consent")) {
                        this.mode = "cookies";
                    } else {
                        this.redirect();
                    }
                }).catch((error) => {
                    this.loginError = (
                        Object.prototype.hasOwnProperty.call(error, "response")
                        && error.response.status === 403
                    ) ? this.i18n.login.error : this.i18n._error;
                });
            },
            /**
             * Redirects user to `inside` SPA.
             * @return {void}
             */
            redirect() {
                const to = sessionStorage.getItem("nav") ? sessionStorage.getItem("nav") : "";
                sessionStorage.removeItem("nav");
                window.location.href = `/inside.html${to}`;
            }
        },
        mounted() {
            if (window.location.search[0] === "?") {
                let params = {};

                window.location.search.slice(1).split("&").forEach((str) => {
                    let split = str.split("=");
                    params[split.shift()] = split.join("=");
                });

                if (params.hasOwnProperty("use")) {
                    const token = params.use;

                    axios.get("/me", {
                        validateStatus: (status => status === 200),
                        headers: {
                            "Authorization": `Bearer ${token}`
                        }
                    }).then((response) => {
                        // noinspection JSUnresolvedVariable
                        if (response.resource !== "customer") {
                            console.error("You are an admin, and admins cannot login to Echoline app directly!");
                            return;
                        }

                        window.localStorage.setItem("token", token);

                        if (!window.localStorage.getItem("cookies_consent")) {
                            this.mode = "cookies";
                        } else {
                            this.redirect();
                        }
                    });
                }
            }
        },
        name: "LoginPage"
    }
</script>
<style lang="scss">
    @import "../../mixins";
    @import '../../colors';

    .login-page {
        position: fixed;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        background-image:
            radial-gradient(circle at top 16% left 16%, #{$color-yellow-dark} 16px, transparent 24px),
            radial-gradient(circle at top 24% right 40%, #{$color-blue-medium} 8px, transparent 16px),
            radial-gradient(circle at top 32% right 16%, #{$color-red-dark} 8px, transparent 16px),
            radial-gradient(circle at bottom 8% right 8%, #{$color-yellow-dark} 8px, transparent 16px),
            radial-gradient(circle at bottom 16% left 50%, #{$color-green-dark} 8px, transparent 16px),
            radial-gradient(circle at top 50% left 0%, #{$color-border}, transparent 50%),
            radial-gradient(circle at bottom right, #{$color-border}, transparent 50%);

        .container-demo {
            position: absolute;
            top: 40px;
            right: 72px;
            margin: 0;
            text-align: right;
            color: $color-font-light;

            a {
                color: $color-mint;
                text-decoration: none;
                @include transition(color);

                &:hover {
                    color: $color-blue-medium;
                }
            }
        }

        form {
            background-color: white;
            border-radius: 16px;
            box-shadow: 0 0 4px black;
            margin: 0 auto;
            width: 40%;
            box-sizing: border-box;

            div {
                display: flex;
                margin-bottom: 56px;
                align-items: center;

                button {
                    line-height: 54px;
                    border: 1px solid $color-mint;
                    background-color: $color-mint;
                    appearance: none;
                    cursor: pointer;
                    padding: 0 19px;
                    font-size: inherit;
                    font-family: inherit;
                    border-radius: 8px;
                    color: white;
                    @include transition(background-color, color);

                    &:hover {
                        background-color: transparent;
                        color: $color-mint;
                    }
                }
            }
        }

        .container-login {
            padding: 0 56px;

            img {
                height: 56px;
                margin: 56px auto;
                display: block;
            }

            input {
                line-height: 24px;
                font-family: inherit;
                font-size: inherit;
                border: 1px solid $color-border;
                padding: 15px 19px;
                border-radius: 8px;
                width: 100%;
                box-sizing: border-box;
                margin-bottom: 32px;

                &:active,
                &:focus {
                    outline: none;
                    border-color: $color-blue-medium;
                }
            }

            .label-error {
                text-align: center;
                line-height: 24px;
                color: $color-red-dark;
                margin: 0 0 32px;
            }

            div {
                justify-content: space-between;

                a {
                    text-decoration: none;
                    color: $color-font-light;
                    cursor: pointer;
                }
            }
        }

        .container-cookies {
            padding: 24px 56px;

            p {
                margin: 32px 0;
            }

            ul {
                line-height: 24px;
                list-style: none;
                margin: 0;
                padding: 0;
            }

            .app-toggle {
                vertical-align: top;
                margin-right: 8px;
            }

            div {
                justify-content: right;
                margin-bottom: 32px;
            }
        }
    }
</style>
